import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import { defaultCountries } from 'shared/react-international-phone/constants/default-countries';
import { DEFAULT_COUNTRY } from 'shared/react-international-phone/constants/default-country';
import { UsePhoneInputConfig, usePhoneInput } from '../../hooks/use-phone-input';
import { buildClassNames } from '../../style/build-class-names';
import { CountryIso2 } from '../../types';
import { getCountry } from '../../utils';
import { CountrySelector, CountrySelectorProps, CountrySelectorStyleProps } from '../country-selector/country-selector';
import { DialCodePreview, DialCodePreviewStyleProps } from '../dial-code-preview/dial-code-preview';
import './phone-input.module.css';
export interface PhoneInputStyleProps {
  style?: React.CSSProperties;
  className?: string;
  inputStyle?: React.CSSProperties;
  inputClassName?: string;
  countrySelectorStyleProps?: CountrySelectorStyleProps;
  dialCodePreviewStyleProps?: DialCodePreviewStyleProps;
}
export interface PhoneInputProps extends Omit<UsePhoneInputConfig, 'onChange'>, PhoneInputStyleProps {
  /**
   * @description Hide the dropdown icon. Make country selection not accessible.
   * @default false
   */
  hideDropdown?: CountrySelectorProps['hideDropdown'];

  /**
   * @description Input's placeholder
   * @default undefined
   */
  placeholder?: React.InputHTMLAttributes<HTMLInputElement>['placeholder'];

  /**
   * @description Disable phone input and country selector.
   * @default false
   */
  disabled?: boolean;

  /**
   * @description
   * Show prefix and dial code between country selector and phone input.
   * Works only when *disableDialCodeAndPrefix* is *true*
   * @default false
   */
  showDisabledDialCodeAndPrefix?: boolean;

  /**
   * @description Default input component props
   * @default undefined
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;

  /**
   * @description Callback that calls on phone change
   * @params `phone` - new phone value, `country` - country iso2 value
   * @default undefined
   */
  onChange?: (phone: string, country: CountryIso2) => void;
}
export const PhoneInput: React.FC<PhoneInputProps> = t0 => {
  const $ = _c(65);
  let className;
  let countrySelectorStyleProps;
  let dialCodePreviewStyleProps;
  let disabled;
  let hideDropdown;
  let inputClassName;
  let inputProps;
  let inputStyle;
  let onChange;
  let placeholder;
  let showDisabledDialCodeAndPrefix;
  let style;
  let t1;
  let t2;
  let t3;
  let usePhoneInputConfig;
  let value;
  if ($[0] !== t0) {
    ({
      hideDropdown,
      placeholder,
      disabled,
      showDisabledDialCodeAndPrefix,
      inputProps,
      onChange,
      style,
      className,
      inputStyle,
      inputClassName,
      countrySelectorStyleProps,
      dialCodePreviewStyleProps,
      value,
      defaultCountry: t1,
      initialCountry: t2,
      countries: t3,
      ...usePhoneInputConfig
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = countrySelectorStyleProps;
    $[3] = dialCodePreviewStyleProps;
    $[4] = disabled;
    $[5] = hideDropdown;
    $[6] = inputClassName;
    $[7] = inputProps;
    $[8] = inputStyle;
    $[9] = onChange;
    $[10] = placeholder;
    $[11] = showDisabledDialCodeAndPrefix;
    $[12] = style;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = usePhoneInputConfig;
    $[17] = value;
  } else {
    className = $[1];
    countrySelectorStyleProps = $[2];
    dialCodePreviewStyleProps = $[3];
    disabled = $[4];
    hideDropdown = $[5];
    inputClassName = $[6];
    inputProps = $[7];
    inputStyle = $[8];
    onChange = $[9];
    placeholder = $[10];
    showDisabledDialCodeAndPrefix = $[11];
    style = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
    usePhoneInputConfig = $[16];
    value = $[17];
  }
  const defaultCountry = t1 === undefined ? DEFAULT_COUNTRY : t1;
  t2 === undefined ? DEFAULT_COUNTRY : t2;
  const countries = t3 === undefined ? defaultCountries : t3;
  let t4;
  if ($[18] !== onChange) {
    t4 = data => {
      onChange?.(data.phone, data.country);
    };
    $[18] = onChange;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== countries || $[21] !== t4 || $[22] !== usePhoneInputConfig || $[23] !== value) {
    t5 = {
      value,
      countries,
      ...usePhoneInputConfig,
      onChange: t4
    };
    $[20] = countries;
    $[21] = t4;
    $[22] = usePhoneInputConfig;
    $[23] = value;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const {
    phone,
    inputRef,
    country,
    setCountry,
    handlePhoneValueChange
  } = usePhoneInput(t5);
  let t6;
  bb0: {
    if (!country) {
      t6 = undefined;
      break bb0;
    }
    let t7;
    if ($[25] !== countries || $[26] !== country || $[27] !== defaultCountry) {
      t7 = getCountry({
        value: country,
        field: "iso2",
        countries,
        defaultCountry
      });
      $[25] = countries;
      $[26] = country;
      $[27] = defaultCountry;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    t6 = t7;
  }
  const fullCountry = t6;
  const showDialCodePreview = usePhoneInputConfig.disableDialCodeAndPrefix && showDisabledDialCodeAndPrefix && fullCountry?.dialCode;
  let t7;
  if ($[29] !== className) {
    t7 = buildClassNames({
      addPrefix: ["input-container"],
      rawClassNames: [className]
    });
    $[29] = className;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== setCountry) {
    t8 = country_0 => setCountry(country_0.iso2);
    $[31] = setCountry;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== countries || $[34] !== country || $[35] !== countrySelectorStyleProps || $[36] !== defaultCountry || $[37] !== disabled || $[38] !== hideDropdown || $[39] !== t8) {
    t9 = <CountrySelector onSelect={t8} defaultCountry={defaultCountry} selectedCountry={country} countries={countries} disabled={disabled} hideDropdown={hideDropdown} {...countrySelectorStyleProps} />;
    $[33] = countries;
    $[34] = country;
    $[35] = countrySelectorStyleProps;
    $[36] = defaultCountry;
    $[37] = disabled;
    $[38] = hideDropdown;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== dialCodePreviewStyleProps || $[42] !== disabled || $[43] !== fullCountry || $[44] !== showDialCodePreview || $[45] !== usePhoneInputConfig.prefix) {
    t10 = showDialCodePreview && <DialCodePreview dialCode={fullCountry.dialCode} prefix={usePhoneInputConfig.prefix ?? "+"} disabled={disabled} {...dialCodePreviewStyleProps} />;
    $[41] = dialCodePreviewStyleProps;
    $[42] = disabled;
    $[43] = fullCountry;
    $[44] = showDialCodePreview;
    $[45] = usePhoneInputConfig.prefix;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  const t11 = disabled && "input--disabled";
  let t12;
  if ($[47] !== inputClassName || $[48] !== t11) {
    t12 = buildClassNames({
      addPrefix: ["input", t11],
      rawClassNames: [inputClassName]
    });
    $[47] = inputClassName;
    $[48] = t11;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== disabled || $[51] !== handlePhoneValueChange || $[52] !== inputProps || $[53] !== inputRef || $[54] !== inputStyle || $[55] !== phone || $[56] !== placeholder || $[57] !== t12) {
    t13 = <input onChange={handlePhoneValueChange} value={phone} type="tel" ref={inputRef} className={t12} placeholder={placeholder} disabled={disabled} style={inputStyle} {...inputProps} />;
    $[50] = disabled;
    $[51] = handlePhoneValueChange;
    $[52] = inputProps;
    $[53] = inputRef;
    $[54] = inputStyle;
    $[55] = phone;
    $[56] = placeholder;
    $[57] = t12;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  let t14;
  if ($[59] !== style || $[60] !== t10 || $[61] !== t13 || $[62] !== t7 || $[63] !== t9) {
    t14 = <div className={t7} style={style}>{t9}{t10}{t13}</div>;
    $[59] = style;
    $[60] = t10;
    $[61] = t13;
    $[62] = t7;
    $[63] = t9;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  return t14;
};